'use client';

import type { CSSProperties } from 'react';
import { BlockIcon, BlockRoot, PortalLink, tx } from '@akademi/site-builder-runtime';

type Localized = string | Record<string, string>;

export interface SolarHeroFeature {
  /** A CURATED icon name. See the note on `features` below. */
  icon?: string;
  title?: Localized;
  description?: Localized;
}

export interface SolarHeroProps {
  kicker?: Localized;
  title?: Localized;
  description?: Localized;
  ctaLabel?: Localized;
  ctaHref?: string;
  secondaryCtaLabel?: Localized;
  secondaryCtaHref?: string;
  image?: string;
  imageAlt?: string;
  features?: SolarHeroFeature[];
}

/**
 * The home page's hero, and the reason it exists rather than reusing
 * `HeroTriColumn`: **it renders the page's `<h1>`**.
 *
 * <p>`HeroTriColumn` renders its title as an `<h2>` and takes no heading-level prop,
 * so the home page — the most linked page on the site — had no `<h1>` at all. Every
 * other content page is fine, because `HeroEditorial` already renders one; the
 * category and product pages render their own. This was the last page without.
 *
 * <p>`HeroTriColumn` lives in the pinned akademi base, which this repository consumes
 * and does not fork, so the fix could not be made there.
 *
 * <p><strong>Icons must be curated names.</strong> `BlockIcon` falls back to lucide's
 * full catalog through `DynamicIcon`, which paints nothing on the server and during
 * the first frame, then swaps the glyph in once its chunk resolves. That is fine below
 * the fold and wrong here: a hero is the LCP element. The curated set renders from a
 * static import and is SSR-safe — `sidebarIcons.tsx` says as much. `file-text` and
 * `truck`, which this hero used before, are valid lucide names and not curated, so
 * they were arriving late on the one surface that cannot afford it.
 */
export function SolarHero({
  kicker,
  title = 'Solar components, priced and shipped to site',
  description,
  ctaLabel = 'Shop components',
  ctaHref = '/categories',
  secondaryCtaLabel,
  secondaryCtaHref,
  image,
  imageAlt = '',
  features = [],
}: SolarHeroProps) {
  return (
    <BlockRoot as="section" style={sectionStyle}>
      <div style={innerStyle}>
        <div style={copyStyle}>
          {tx(kicker) ? <span style={kickerStyle}>{tx(kicker)}</span> : null}
          <h1 style={titleStyle}>{tx(title)}</h1>
          {tx(description) ? <p style={bodyStyle}>{tx(description)}</p> : null}
          <div style={ctaRowStyle}>
            {tx(ctaLabel) ? (
              <PortalLink href={ctaHref} style={{ ...ctaStyle, ...primaryCtaStyle }}>
                {tx(ctaLabel)}
              </PortalLink>
            ) : null}
            {tx(secondaryCtaLabel) && secondaryCtaHref ? (
              <PortalLink href={secondaryCtaHref} style={{ ...ctaStyle, ...secondaryStyle }}>
                {tx(secondaryCtaLabel)}
              </PortalLink>
            ) : null}
          </div>
        </div>

        {image ? (
          // Not next/image: a block is rendered through the site-builder registry and
          // receives a URL the operator typed, which may be off-origin and unconfigured.
          // eslint-disable-next-line @next/next/no-img-element
          <img src={image} alt={imageAlt} style={imageStyle} />
        ) : null}
      </div>

      {features.length ? (
        <ul style={featuresStyle}>
          {features.map((feature, index) => (
            <li key={index} style={featureStyle}>
              {feature.icon ? (
                <span aria-hidden style={featureIconStyle}>
                  <BlockIcon value={feature.icon} size={20} />
                </span>
              ) : null}
              <div>
                {tx(feature.title) ? <p style={featureTitleStyle}>{tx(feature.title)}</p> : null}
                {tx(feature.description) ? (
                  <p style={featureBodyStyle}>{tx(feature.description)}</p>
                ) : null}
              </div>
            </li>
          ))}
        </ul>
      ) : null}
    </BlockRoot>
  );
}

const sectionStyle: CSSProperties = {
  padding: 'clamp(32px, 5vw, 64px) var(--space-6, 24px) 0',
};
const innerStyle: CSSProperties = {
  maxWidth: 1180,
  margin: '0 auto',
  display: 'grid',
  gap: 'clamp(24px, 4vw, 48px)',
  gridTemplateColumns: 'repeat(auto-fit, minmax(320px, 1fr))',
  alignItems: 'center',
};
const copyStyle: CSSProperties = { maxWidth: 620 };
const kickerStyle: CSSProperties = {
  color: 'var(--color-text-brand, #880a1e)',
  fontSize: 'var(--font-size-sm, 14px)',
  fontWeight: 700,
  letterSpacing: '.08em',
  textTransform: 'uppercase',
};
const titleStyle: CSSProperties = {
  margin: '10px 0 14px',
  fontSize: 'clamp(26px, 2.6vw, 34px)',
  lineHeight: 1.06,
  fontFamily: 'var(--font-family-display, inherit)',
};
const bodyStyle: CSSProperties = {
  margin: '0 0 24px',
  color: 'var(--color-text-secondary, #475569)',
  fontSize: 'clamp(16px, 1.3vw, 18px)',
  lineHeight: 1.6,
};
const ctaRowStyle: CSSProperties = { display: 'flex', flexWrap: 'wrap', gap: 12 };
const ctaStyle: CSSProperties = {
  display: 'inline-flex',
  alignItems: 'center',
  gap: 8,
  padding: '13px 22px',
  borderRadius: 'var(--ae-radius, 3px)',
  fontWeight: 700,
  textDecoration: 'none',
};
const primaryCtaStyle: CSSProperties = {
  background: 'var(--color-brand-500, #c8102e)',
  color: '#fff',
};
const secondaryStyle: CSSProperties = {
  border: '1px solid var(--color-border-default, #cbd5e1)',
  color: 'var(--color-text-primary, #0f172a)',
};
const imageStyle: CSSProperties = {
  width: '100%',
  height: '100%',
  maxHeight: 420,
  objectFit: 'cover',
  borderRadius: 'var(--ae-radius, 3px)',
};
const featuresStyle: CSSProperties = {
  maxWidth: 1180,
  margin: 'clamp(28px, 4vw, 48px) auto 0',
  padding: 0,
  listStyle: 'none',
  display: 'grid',
  gap: 20,
  gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))',
  borderTop: '1px solid var(--color-border-default, #e2e8f0)',
  paddingTop: 'clamp(20px, 3vw, 32px)',
};
const featureStyle: CSSProperties = { display: 'flex', gap: 12, alignItems: 'flex-start' };
const featureIconStyle: CSSProperties = {
  display: 'inline-flex',
  color: 'var(--color-text-brand, #880a1e)',
  flexShrink: 0,
};
const featureTitleStyle: CSSProperties = { margin: '0 0 4px', fontWeight: 700 };
const featureBodyStyle: CSSProperties = {
  margin: 0,
  color: 'var(--color-text-secondary, #475569)',
  fontSize: 'var(--font-size-sm, 14px)',
  lineHeight: 1.55,
};
